import { OptionsObject, useSnackbar, VariantType } from 'notistack';
import * as shortId from 'shortid';

import DetailedSnackMessage from '../DetailedSnackMessage/DetailedSnackMessage';
import SimpleSnackMessage from '../SimpleSnackMessage/SimpleSnackMessage';
import { EnqueSimpleSnackbar, SnackbarVariantFunction } from '../types';
import { SNACK_AUTO_HIDE_DELAY } from './constants';
import { useCallback, useMemo } from 'react';

export function useCustomSnackbar() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const enqueueCustomSnackbar = useCallback(
    (message: string, options: OptionsObject) => {
      const key = shortId.generate();
      return enqueueSnackbar(null, {
        ...options,
        key,
        autoHideDuration: SNACK_AUTO_HIDE_DELAY,
        content: () =>
          options?.content ? (
            <DetailedSnackMessage
              Content={options.content}
              message={message}
              closeSnackbar={() => closeSnackbar(key)}
              variant={options.variant}
            />
          ) : (
            <SimpleSnackMessage
              message={message}
              closeSnackbar={() => closeSnackbar(key)}
              variant={options.variant}
            />
          ),
      });
    },
    [closeSnackbar, enqueueSnackbar]
  );

  const getVariantFunction = useCallback(
    (variant: VariantType): SnackbarVariantFunction =>
      (message, options = {}) =>
        enqueueCustomSnackbar(message, { ...options, variant }),

    [enqueueCustomSnackbar]
  );

  const withHelpers = useMemo(() => {
    const method = enqueueCustomSnackbar as EnqueSimpleSnackbar;
    method.error = getVariantFunction('error');
    method.success = getVariantFunction('success');
    method.warning = getVariantFunction('warning');
    method.info = getVariantFunction('info');
    return method;
  }, [enqueueCustomSnackbar, getVariantFunction]);

  return { enqueueCustomSnackbar: withHelpers };
}
